import toolBrands from './toolBrands.json'
import toolIconMeta from './toolIconMeta.json'

export type BrandIcon = { slug: string; title: string; hex: string }

export type GenericIconName =
  | 'sparkles'
  | 'plug'
  | 'monitor'
  | 'language'
  | 'keyboard'
  | 'globe'
  | 'search'
  | 'chart'
  | 'layers'
  | 'pen'
  | 'workflow'
  | 'cloud'
  | 'card'
  | 'database'
  | 'video'
  | 'image'
  | 'code'

export type ToolIcon =
  | { kind: 'brand'; label: string; icon: BrandIcon }
  | { kind: 'generic'; label: string; icon: GenericIconName }

const META: Record<string, { title: string; hex: string }> = toolIconMeta

function brandIcon(slug: string): BrandIcon {
  const meta = META[slug]
  if (!meta) throw new Error(`Run pnpm icons: ${slug} is missing from the tool icon sprite.`)
  return { slug, ...meta }
}

const BRANDS: Record<string, string> = toolBrands.brands

export const SOCIAL_ICONS = {
  github: brandIcon(toolBrands.social.github),
  youtube: brandIcon(toolBrands.social.youtube),
  x: brandIcon(toolBrands.social.x),
}

const GENERIC: Record<string, GenericIconName> = {
  'ai agents': 'sparkles',
  automation: 'workflow',
  rag: 'database',
  openai: 'sparkles',
  'openai api': 'sparkles',
  apis: 'plug',
  aws: 'cloud',
  capcut: 'video',
  'capcut pro': 'video',
  'runway ml': 'video',
  'ui/ux': 'layers',
  lovable: 'code',
  midjourney: 'image',
  canva: 'image',
  photoshop: 'image',
  'adobe photoshop': 'image',
  illustrator: 'pen',
  'adobe illustrator': 'pen',
  'leonardo ai': 'image',
  seo: 'search',
  'content strategy': 'pen',
  ubersuggest: 'chart',
  chatgpt: 'sparkles',
  geo: 'sparkles',
  'computer basics': 'monitor',
  english: 'language',
  typing: 'keyboard',
  'google workspace': 'globe',
  internet: 'globe',
  sslcommerz: 'card',
  bkash: 'card',
  'vs code': 'code',
  pinecone: 'database',
  chromadb: 'database',
  'retell ai': 'sparkles',
  'adobe premiere pro': 'video',
  descript: 'video',
  submagic: 'video',
  'opus clip': 'video',
  figjam: 'layers',
  'adobe express': 'image',
  frase: 'pen',
  langsmith: 'workflow',
  windows: 'monitor',
  macos: 'monitor',
  'macos basics': 'monitor',
  zoom: 'video',
  bangla: 'language',
  'english typing': 'keyboard',
}

function normalize(name: string) {
  return name
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

export function isKnownTool(name: string) {
  const key = normalize(name)
  return key in BRANDS || key in GENERIC
}

export function toolIcon(name: string): ToolIcon {
  const key = normalize(name)
  const slug = BRANDS[key]
  if (slug) return { kind: 'brand', label: name, icon: brandIcon(slug) }
  return { kind: 'generic', label: name, icon: GENERIC[key] ?? 'code' }
}

export function splitTool(tool: string) {
  return tool
    .replace(/\([^)]*\)/g, '')
    .split(/\s+(?:\/|&|\+)\s+|\s*,\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
}

export function toolIcons(tool: string): ToolIcon[] {
  return splitTool(tool).map(toolIcon)
}

export function iconKey(icon: ToolIcon) {
  return icon.kind === 'brand' ? `brand:${icon.icon.slug}` : `generic:${icon.icon}`
}

export function distinctIcons(icons: ToolIcon[]) {
  const seen = new Set<string>()
  return icons.filter((icon) => {
    const key = iconKey(icon)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export type BrandTool = { name: string; icon: BrandIcon }

export function brandTools(tools: string[]): BrandTool[] {
  const seen = new Set<string>()
  const result: BrandTool[] = []
  for (const tool of tools) {
    for (const icon of toolIcons(tool)) {
      if (icon.kind !== 'brand' || seen.has(icon.icon.title)) continue
      seen.add(icon.icon.title)
      result.push({ name: icon.icon.title, icon: icon.icon })
    }
  }
  return result
}

function luminance(hex: string) {
  const value = Number.parseInt(hex, 16)
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => {
    const scaled = channel / 255
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function brandColorOnDark(hex: string) {
  return luminance(hex) < 0.07 ? '#E6EAF0' : `#${hex}`
}
